// Copyright (c) Ping. All rights reserved.
//
// Dependency-free, cross-platform Tab View — a drop-in alternative to
// `react-native-tab-view` that runs identically on react-native-web AND
// react-native-macos because it uses ONLY React Native core primitives
// (`Animated`, `PanResponder`, `View`). No `react-native-pager-view` /
// `react-native-reanimated` (neither supports macOS cleanly), so there's no
// native-build risk on the desktop target.
//
// Parity with the library's headline behaviour:
//   • All scenes are mounted by default and STAY mounted across switches —
//     switching tabs is a pure transform offset, so lists keep their scroll
//     position, avatars don't reload, and nothing re-fetches. `lazy` defers
//     the first render of off-screen scenes (then keeps them mounted).
//   • Animated horizontal slide between scenes (spring), controlled `index`.
//   • Optional swipe gesture (`swipeEnabled`) that yields to vertical scroll
//     (only a horizontally-biased drag captures the responder).
//   • Optional animated `TabBar` (rendered by default; pass `renderTabBar`
//     to customise, or `() => null` to drive selection from outside).
//
// Layout uses percentage widths for the scene row so the FIRST paint is
// correct even before the container width is measured; the px translate that
// powers the slide + swipe is derived from the measured width via `onLayout`.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Animated,
  Easing,
  PanResponder,
  type StyleProp,
  View,
  type ViewStyle,
} from 'react-native';
import { TabBar } from './TabBar';

export interface TabViewRoute {
  /** Stable identity for the scene (React key). */
  readonly key: string;
  /** Label shown in the default `TabBar`. */
  readonly title?: string;
  /** Optional count badge shown in the default `TabBar`. */
  readonly badge?: number;
}

export interface TabBarRenderProps {
  readonly routes: readonly TabViewRoute[];
  readonly index: number;
  readonly onIndexChange: (index: number) => void;
  /** Live pager offset (px, negative) — drives the animated indicator. */
  readonly position: Animated.Value;
  /** Measured container width (px); 0 until first layout. */
  readonly layoutWidth: number;
}

export interface TabViewProps {
  readonly routes: readonly TabViewRoute[];
  /** Controlled active index. */
  readonly index: number;
  readonly onIndexChange: (index: number) => void;
  readonly renderScene: (info: {
    readonly route: TabViewRoute;
    readonly index: number;
    readonly focused: boolean;
  }) => ReactNode;
  /** Custom tab bar. Return `null` to render none (drive selection yourself). */
  readonly renderTabBar?: (props: TabBarRenderProps) => ReactNode;
  readonly tabBarPosition?: 'top' | 'bottom';
  /** Defer first render of off-screen scenes; they stay mounted afterwards. */
  readonly lazy?: boolean;
  /** Enable horizontal swipe between scenes (default true). */
  readonly swipeEnabled?: boolean;
  /** Animate the slide on index change (default true). */
  readonly animationEnabled?: boolean;
  /** When set, the slide uses a fixed-duration ease (ms) instead of the default
   *  snappy spring — for a more pronounced, deliberate sweep. */
  readonly slideDurationMs?: number;
  readonly style?: StyleProp<ViewStyle>;
  readonly sceneContainerStyle?: StyleProp<ViewStyle>;
}

// react-native-web's Animated has no native driver; macOS does. Gesture-driven
// values are written from JS (`setValue` during a pan), so we keep the pager on
// the JS driver everywhere to avoid native/JS value contention.
const USE_NATIVE_DRIVER = false;

export function TabView({
  routes,
  index,
  onIndexChange,
  renderScene,
  renderTabBar,
  tabBarPosition = 'top',
  lazy = false,
  swipeEnabled = true,
  animationEnabled = true,
  slideDurationMs,
  style,
  sceneContainerStyle,
}: TabViewProps): JSX.Element {
  const count = routes.length;
  const [layoutWidth, setLayoutWidth] = useState(0);
  const translateX = useRef(new Animated.Value(0)).current;

  // Mirror the latest index/width into refs so the PanResponder (created once)
  // always reads current values without being re-created mid-gesture.
  const indexRef = useRef(index);
  indexRef.current = index;
  const widthRef = useRef(layoutWidth);
  widthRef.current = layoutWidth;
  const prevIndexRef = useRef(index);

  // Lazy bookkeeping — once visited, a scene stays in the mounted set.
  const [visited, setVisited] = useState<ReadonlySet<number>>(
    () => new Set([index]),
  );
  useEffect(() => {
    setVisited((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
  }, [index]);

  const animateTo = useCallback(
    (target: number, velocity = 0): void => {
      const w = widthRef.current;
      const to = -target * w;
      if (!animationEnabled || w === 0) {
        translateX.setValue(to);
        return;
      }
      // A fixed-duration ease when `slideDurationMs` is set — a more pronounced,
      // deliberate sweep (used by the conversations↔activity + active↔archived
      // swaps). Otherwise a snappy, critically-damped spring (near-instant).
      if (slideDurationMs !== undefined) {
        Animated.timing(translateX, {
          toValue: to,
          duration: slideDurationMs,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: USE_NATIVE_DRIVER,
        }).start();
        return;
      }
      Animated.spring(translateX, {
        toValue: to,
        velocity,
        stiffness: 600,
        damping: 38,
        mass: 0.6,
        restDisplacementThreshold: 0.5,
        restSpeedThreshold: 0.5,
        useNativeDriver: USE_NATIVE_DRIVER,
      }).start();
    },
    [animationEnabled, slideDurationMs, translateX],
  );

  // Slide when the controlled index changes; snap (no animation) when only the
  // width changed (e.g. the panel is being drag-resized) so it never lags.
  useEffect(() => {
    const indexChanged = prevIndexRef.current !== index;
    prevIndexRef.current = index;
    if (layoutWidth === 0) {
      translateX.setValue(-index * layoutWidth);
      return;
    }
    if (indexChanged && animationEnabled) {
      animateTo(index);
    } else {
      translateX.setValue(-index * layoutWidth);
    }
  }, [index, layoutWidth, animationEnabled, animateTo, translateX]);

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        // Only claim the gesture for a clearly-horizontal drag so a vertical
        // scroll inside a scene keeps working.
        onMoveShouldSetPanResponder: (_e, g) =>
          swipeEnabled &&
          count > 1 &&
          widthRef.current > 0 &&
          Math.abs(g.dx) > Math.abs(g.dy) * 1.5 &&
          Math.abs(g.dx) > 8,
        onPanResponderGrant: () => {
          translateX.stopAnimation((v: number) => {
            startOffsetRef.current = v;
          });
        },
        onPanResponderMove: (_e, g) => {
          const w = widthRef.current;
          const min = -(count - 1) * w;
          const max = 0;
          let next = startOffsetRef.current + g.dx;
          // Rubber-band past the edges.
          if (next > max) next = max + (next - max) * 0.3;
          else if (next < min) next = min + (next - min) * 0.3;
          translateX.setValue(next);
        },
        onPanResponderRelease: (_e, g) => {
          const w = widthRef.current;
          if (w === 0) return;
          const cur = indexRef.current;
          const threshold = w * 0.3;
          let target = cur;
          if (g.dx <= -threshold || g.vx < -0.3) target = Math.min(cur + 1, count - 1);
          else if (g.dx >= threshold || g.vx > 0.3) target = Math.max(cur - 1, 0);
          animateTo(target, g.vx);
          if (target !== cur) onIndexChange(target);
        },
        onPanResponderTerminate: () => animateTo(indexRef.current),
      }),
    [swipeEnabled, count, onIndexChange, animateTo, translateX],
  );
  const startOffsetRef = useRef(0);

  const tabBarProps: TabBarRenderProps = {
    routes,
    index,
    onIndexChange,
    position: translateX,
    layoutWidth,
  };
  const tabBar =
    renderTabBar !== undefined ? (
      renderTabBar(tabBarProps)
    ) : (
      <TabBar {...tabBarProps} />
    );

  return (
    <View
      style={[styles.root, style]}
      onLayout={(e) => setLayoutWidth(e.nativeEvent.layout.width)}
    >
      {tabBarPosition === 'top' ? tabBar : null}
      <View style={styles.pagerClip} {...(swipeEnabled ? panResponder.panHandlers : {})}>
        <Animated.View
          style={[
            styles.row,
            { width: `${count * 100}%`, transform: [{ translateX }] },
          ]}
        >
          {routes.map((route, i) => {
            const focused = i === index;
            const shouldRender = !lazy || visited.has(i);
            return (
              <View
                key={route.key}
                style={[{ width: `${100 / count}%` }, styles.scene, sceneContainerStyle]}
                // Off-screen scenes stay mounted but can't be clicked/focused
                // through the clip — keeps interaction confined to the active
                // scene without unmounting the others.
                pointerEvents={focused ? 'auto' : 'none'}
              >
                {shouldRender ? renderScene({ route, index: i, focused }) : null}
              </View>
            );
          })}
        </Animated.View>
      </View>
      {tabBarPosition === 'bottom' ? tabBar : null}
    </View>
  );
}

const styles = {
  root: { flex: 1, minHeight: 0, overflow: 'hidden' as const },
  pagerClip: { flex: 1, minHeight: 0, overflow: 'hidden' as const },
  row: { flex: 1, flexDirection: 'row' as const, minHeight: 0 },
  // Each scene fills the pager height (cross-axis stretch) at its fixed width.
  scene: { minHeight: 0, alignSelf: 'stretch' as const },
} satisfies Record<string, ViewStyle>;
